import axios, { AxiosError, type AxiosRequestConfig } from 'axios';
import { authStore } from '../state/auth-store';
import { tokenStore } from '../state/token-store';
import { disconnectSocket } from './socket';

declare global {
  interface Window {
    __ENV__?: { API_URL?: string };
  }
}

/**
 * API base URL resolution (runtime-first, build-time fallback):
 *  1. window.__ENV__.API_URL (runtime injection via env-config.js) → appends /api
 *  2. VITE_API_BASE (build-time env var)
 *  3. '/api' (relative — requires reverse proxy)
 */
export const API_BASE = window.__ENV__?.API_URL
  ? `${window.__ENV__.API_URL.replace(/\/+$/, '')}/api`
  : (import.meta.env.VITE_API_BASE ?? '/api');

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const client = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  timeout: 15_000,
  headers: { 'Content-Type': 'application/json' },
});

let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  if (isRefreshing) return refreshPromise!;
  isRefreshing = true;
  refreshPromise = client
    .post('/auth/refresh', undefined, { _retry: true } as any)
    .then(() => true)
    .catch(() => false)
    .finally(() => { isRefreshing = false; refreshPromise = null; });
  return refreshPromise;
}

/**
 * Endpoints that answer 401 as a NORMAL result: a wrong password, or a bootstrap
 * probe before any session exists. A 401 from these is not a dead session, so it
 * must neither trigger the refresh-and-retry dance nor tear the session down.
 */
const AUTH_ENDPOINTS = [
  '/auth/login', '/auth/register', '/auth/refresh',
  '/auth/forgot-password', '/auth/reset-password',
];
const isAuthEndpoint = (url?: string): boolean =>
  !!url && AUTH_ENDPOINTS.some((p) => url === p || url.startsWith(p + '?') || url.startsWith(p + '/'));

/**
 * The refresh token is gone — expired, revoked, or cleared by the server — so
 * the session is genuinely over. Drop the local traces of it; `main.ts`
 * subscribes to authStore and routes a session-less user off /play to /login.
 *
 * Without this the SPA kept running against dead cookies: every later request
 * 401'd and each screen rendered its own "Unauthorized", so the player sat in a
 * zombie app — stale numbers presented as live, every action failing, no way
 * back to the login screen short of a manual reload. Reported against Mafinini
 * as "won the bout but got no rating, then the error spread to every section".
 */
function endSession(): void {
  tokenStore.clear();
  disconnectSocket();
  if (authStore.get().user === null) return; // already ended — stay idempotent
  authStore.set({ user: null, status: 'idle', error: null });
}

client.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const original = error.config as AxiosRequestConfig & { _retry?: boolean };
    if (error.response?.status === 401 && !original._retry && !isAuthEndpoint(original.url)) {
      original._retry = true;
      const refreshed = await tryRefresh();
      if (refreshed) return client(original);
      endSession();
    }
    const data = error.response?.data as Record<string, unknown> | undefined;
    throw new ApiError(
      error.response?.status ?? 0,
      (data?.error as string) ?? 'Error',
      (data?.message as string) ?? error.message,
      data,
    );
  },
);

export interface RequestOptions {
  headers?: Record<string, string>;
  skipAuthRetry?: boolean;
  signal?: AbortSignal;
}

function mergeConfig(opts?: RequestOptions): AxiosRequestConfig {
  const cfg: AxiosRequestConfig = {};
  if (opts?.headers) cfg.headers = opts.headers;
  if (opts?.signal) cfg.signal = opts.signal;
  if (opts?.skipAuthRetry) (cfg as any)._retry = true;
  return cfg;
}

export const api = {
  get: <T>(p: string, o?: RequestOptions) =>
    client.get<T>(p, mergeConfig(o)).then((r) => r.data),
  post: <T>(p: string, body?: unknown, o?: RequestOptions) =>
    client.post<T>(p, body, mergeConfig(o)).then((r) => r.data),
  patch: <T>(p: string, body?: unknown, o?: RequestOptions) =>
    client.patch<T>(p, body, mergeConfig(o)).then((r) => r.data),
  put: <T>(p: string, body?: unknown, o?: RequestOptions) =>
    client.put<T>(p, body, mergeConfig(o)).then((r) => r.data),
  del: <T>(p: string, o?: RequestOptions) =>
    client.delete<T>(p, mergeConfig(o)).then((r) => r.data),
};
